import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Button, Container } from '@cof/omni-react';
import { RoundedButton } from '@components/global';
import styled from '@emotion/styled';

const StyledPullDialog = styled('dialog')`
  & {
    padding-top: 0;
    max-width: 100%;
    max-height: 75vh;
    height: 100%;
    width: 100%;
    position: fixed;
    bottom: -75vh;
    top: auto;
    left: 0;
    right: auto;
    border: none;
    transition:
      bottom 0.5s ease-in-out,
      overlay 0.5s ease-out allow-discrete,
      display 0.5s ease-out allow-discrete;
  }
  ,
  &.openModal {
    bottom: 0;
  }
  ,
  &::backdrop {
    background-color: rgb(0 0 0, 0.3);
    transition:
      display 0.5s allow-discrete,
      overlay 0.5s allow-discrete,
      background-color 0.5s ease-out;
  }
  ,
  &[open]::backdrop {
    background-color: rgba(0, 0, 0, 0.3);
  }
`;

const PullButton = styled(Button)`
  & {
    padding: 16px 0 32px;
    width: 100%;
  }
  ,
  &:hover,
  &:before,
  &:after {
    border: none;
    background: none;
  }
  ,
  &:not(:disabled):active,
  &:not(:disabled):focus,
  &:not(:disabled):hover {
    border: none;
    background: none;
  }
`;

export const PullDialog = ({
  children,
  ...restOfProps
}: {
  children: ReactNode;
}) => {
  const modalContainer = useRef<HTMLDialogElement | null>(null);
  const [modalClassName, setModalClassName] = useState('');

  const toggleDialog = useCallback(
    (e: React.MouseEvent<HTMLButtonElement> | Event) => {
      e.preventDefault();
      if (modalContainer.current) {
        if (modalContainer.current.getAttribute('open') !== null) {
          modalContainer.current.close();
          setModalClassName('');
        } else {
          modalContainer.current.showModal();
          setModalClassName('openModal');
        }
      }
    },
    [modalContainer],
  );

  useEffect(() => {
    const curr = modalContainer.current;
    if (curr && curr.firstElementChild) {
      curr.firstElementChild.addEventListener(
        'touchmove',
        (e) => toggleDialog(e),
        { passive: false },
      );
    }
    return () => {
      curr &&
        curr.firstElementChild &&
        curr.firstElementChild.removeEventListener(
          'touchmove',
          (e) => toggleDialog(e),
          false,
        );
    };
  }, [modalContainer, toggleDialog]);

  return (
    <Container>
      <div style={{ padding: '1rem', width: '65%' }}>
        <RoundedButton
          btnVariant="secondary"
          displayText="Activate New Card"
          onClickCallback={toggleDialog}
          data-testid="OpenPullDialog"
        />
      </div>
      <StyledPullDialog
        ref={modalContainer}
        aria-modal="true"
        role="dialog"
        className={modalClassName}
        data-testid="Dialog--PullDialog"
        {...restOfProps}
      >
        <PullButton
          onClick={toggleDialog}
          type="button"
          aria-label="close"
          rank="tertiary"
          data-testid="Close-Button"
        >
          <svg
            width="44"
            height="4"
            viewBox="0 0 44 4"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect width="44" height="4" rx="2" fill="#949494" />
          </svg>
        </PullButton>
        {children}
      </StyledPullDialog>
    </Container>
  );
};
